import { Fragment } from "react";
import { useHasAccess } from "../hooks/useHasAccess";
import { AiProviderModal } from "./AiProviderModal";
import { ConnectorModal } from "./ConnectorModal";
import { CreateSpaceModal } from "./CreateSpaceModal";
import { CustomizeNavigationModal } from "./CustomizeNavigationModal";
import { FileBrowser } from "./FileBrowser";
import { WorkflowValueModal } from "./Monitoring/WorkflowValueModal";
import { closeModal, type ModalEntry, useModalStack } from "./globalModalStack";
import { MemberDetailModalById } from "./Settings/MembersSettings/MemberDetailModalById";
import { NetworkDetailModal } from "./Settings/NetworksSettings/NetworkDetailModal";
import { InspectRoleModalById } from "./Settings/RolesSettings/InspectRoleModalById";
import { ServiceAccountDetail } from "./Settings/ServiceAccountsSettings/ServiceAccountDetail";
import { TenantAdminSettings } from "./Settings/UserGroupsSettings/TenantAdminSettings";
import { UserGroupSettings } from "./Settings/UserGroupsSettings/UserGroupSettings";
import { SkillModal } from "./SkillModal";
import { SpaceSettings } from "./SpaceSettings";

function renderModal(
  entry: ModalEntry,
  canOpenAdmins: boolean,
  canCreateSpace: boolean
) {
  const onClose = () => closeModal();
  switch (entry.type) {
    case "space":
      return <SpaceSettings mode="edit" spaceId={entry.id} onClose={onClose} />;
    case "createSpace":
      if (!canCreateSpace) return null;
      return (
        <CreateSpaceModal
          workflowId={entry.values.workflowId}
          onClose={onClose}
        />
      );
    case "connector":
      return <ConnectorModal id={entry.id} onClose={onClose} />;
    case "aiProvider":
      return <AiProviderModal id={entry.id} onClose={onClose} />;
    case "workflowValue":
      return <WorkflowValueModal id={entry.id} onClose={onClose} />;
    case "group":
      return <UserGroupSettings groupId={entry.id} onClose={onClose} />;
    case "member":
      return <MemberDetailModalById userId={entry.id} onClose={onClose} />;
    case "role":
      return <InspectRoleModalById roleId={entry.id} onClose={onClose} />;
    case "skill":
      return <SkillModal id={entry.id} onClose={onClose} />;
    case "network":
      return <NetworkDetailModal networkId={entry.id} onClose={onClose} />;
    case "serviceAccount":
      return (
        <ServiceAccountDetail serviceAccountId={entry.id} onClose={onClose} />
      );
    case "admins":
      if (!canOpenAdmins) return null;
      return <TenantAdminSettings onClose={onClose} />;
    case "customizeNav":
      return <CustomizeNavigationModal />;
    case "storage": {
      const { spaceId, branchId, workflowId, volume, section } = entry.values;
      if (!spaceId) return null;
      return (
        <FileBrowser
          spaceId={spaceId}
          branchId={branchId}
          workflowId={workflowId}
          volume={volume}
          section={section}
          onClose={onClose}
        />
      );
    }
  }
}

export function GlobalModals() {
  const stack = useModalStack();
  const canOpenAdmins = useHasAccess("allow_all");
  const canCreateSpace = useHasAccess("tenant:space_create");

  return (
    <>
      {stack.map((entry) => (
        <Fragment key={entry.key}>
          {renderModal(entry, canOpenAdmins, canCreateSpace)}
        </Fragment>
      ))}
    </>
  );
}
